<script setup lang="ts">
import { useI18n } from "vue-i18n";

import { useGlobalStore } from "@/shared/model";
import { CircleCrossIcon, CrossIcon, RefreshIcon } from "@/shared/ui";

import { useResetTradeIn } from "../model";

const { t } = useI18n({ useScope: "global" });
const globalStore = useGlobalStore();
const { restart, close } = useResetTradeIn();

/** The menu is open while focused, so the focus is dropped to close it after the action */
function select(action: () => void, event: MouseEvent): void {
    (event.currentTarget as HTMLElement).blur();
    action();
}
</script>

<!-- Крестик в углу виджета: при наведении или фокусе открывает меню "Начать заново" / "Закрыть" -->
<template>
    <div class="close-menu">
        <button type="button" class="close-menu__toggle" :aria-label="t('common.close')">
            <CrossIcon />
        </button>

        <div class="close-menu__dropdown">
            <ul class="close-menu__list">
                <li>
                    <button type="button" class="close-menu__item" @click="select(restart, $event)">
                        <RefreshIcon />
                        {{ t("common.restart") }}
                    </button>
                </li>
                <!-- An embedded widget is a part of the partner page and can't be closed -->
                <li v-if="globalStore.modeIsPopup">
                    <button
                        type="button"
                        class="close-menu__item close-menu__item_danger"
                        @click="select(close, $event)"
                    >
                        <CircleCrossIcon />
                        {{ t("common.close") }}
                    </button>
                </li>
            </ul>
        </div>
    </div>
</template>

<style scoped>
/* The container is only as big as the toggle, so the hidden dropdown doesn't catch the hover */
.close-menu {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 10;
}

.close-menu__toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background-color: #f4f6f8;
    color: var(--custom-color-gray);
    cursor: pointer;
    transition:
        color 150ms ease,
        transform 150ms ease;

    svg {
        width: 14px;
        height: 14px;
    }
}

/* The padding bridges the gap to the toggle, so the cursor doesn't lose the hover on the way down */
.close-menu__dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    width: max-content;
    padding-top: 8px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 150ms ease;
}

.close-menu__list {
    margin: 0;
    padding: 10px 0;
    border-radius: 8px;
    background-color: #fff;
    box-shadow:
        0 -2px 8px 0 rgb(0 0 0 / 15%),
        0 2px 8px 0 rgb(0 0 0 / 15%);
    list-style: none;
    user-select: none;
}

.close-menu:hover,
.close-menu:focus-within {
    .close-menu__toggle {
        color: var(--custom-color-main-hover, var(--custom-color-main));
        transform: rotate(90deg);
    }

    .close-menu__dropdown {
        opacity: 1;
        pointer-events: auto;
    }
}

.close-menu__item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 6px 16px;
    border: none;
    background: none;
    color: var(--custom-color-text);
    font: inherit;
    font-size: var(--custom-font-size);
    line-height: 1.429;
    white-space: nowrap;
    cursor: pointer;
    transition: background-color 150ms ease;

    &:hover,
    &:focus-visible {
        background-color: #f4f6f8;
        outline: none;
    }
}

.close-menu__item_danger {
    color: var(--color-error);
}
</style>
